import { CircularProgress, Paper, Typography } from '@material-ui/core'
import { useStyles } from './Styles'
import { ExecutionDetails } from '../../../shared/src/data/executionDetails'
import ExecutionItem from './ExecutionItem'
import { useEffect, useState } from 'react'
import { PlayExecutions } from './PlayExecution'
import { useExecutions } from '../steps/queries/executions'
import PastExecutionItem from './PastExecutionItem'
import { useEngineStateQuery } from '../steps/engineState/queries/engineState'

interface ExecutionListProps {
  title: string
  executions: ExecutionDetails[]
  withActions?: boolean
  isLoading: boolean
  error?: Error | null
  refetchPendingExecutions?: () => void
}

export default function ExecutionList({
  title,
  executions,
  withActions = false,
  isLoading,
  error,
  refetchPendingExecutions = () => {},
}: ExecutionListProps): JSX.Element {
  const styles = useStyles()
  const [selectedExecutions, setSelectedExecutions] = useState<Set<string>>(
    new Set(),
  )
  const { queuedExecutions } = useExecutions()
  useEffect(() => {
    const newSelectedExecutions = new Set(
      queuedExecutions.map((execution) => execution.id),
    )
    setSelectedExecutions(newSelectedExecutions)
  }, [queuedExecutions])

  const addSelectedExecution = (id: string) => {
    setSelectedExecutions((prevSelected) => {
      const newSelectedExecutions = new Set(prevSelected)
      newSelectedExecutions.add(id)
      return newSelectedExecutions
    })
  }

  const removeSelectedExecution = (id: string) => {
    setSelectedExecutions((prevSelected) => {
      const newSelectedExecutions = new Set(prevSelected)
      newSelectedExecutions.delete(id)
      return newSelectedExecutions
    })
  }

  const { data: engineStatus, isLoading: engineStateLoading, error: engineStateError } = useEngineStateQuery();
  const remoteRunning = engineStatus === 'RUNNING';
  if (!remoteRunning && localStorage.getItem('playButtonClicked') === 'true') {
    localStorage.setItem('playButtonClicked', 'false');
  }
  return (
    <Paper className={styles.executionListContainer} elevation={3}>
      <Typography
        variant='h6'
        className={styles.header}
        style={{ textAlign: 'center' }}
      >
        {title}
      </Typography>
      <div style={{ minHeight: '50px' }}>
        {withActions && (
          <PlayExecutions
            refetchPendingExecutions={refetchPendingExecutions}
            hasQueued={queuedExecutions && queuedExecutions.length !== 0}
            selected={Array.from(selectedExecutions.values())}
            remoteRunning={remoteRunning}
          />
        )}
      </div>
      {isLoading || engineStateLoading ? (
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <CircularProgress />
        </div>
      ) : error || engineStateError ? (
        <Typography
          color='error'
          style={{ textAlign: 'center', marginTop: '16px' }}
        >
          Error loading executions
        </Typography>
      ) : executions && executions.length > 0 ? (
        executions.map((execution, index) => (
          <div
            key={index}
            style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}
          >
            {withActions ? (
              <ExecutionItem
              remoteRunning={remoteRunning}
                execution={execution}
                checked={selectedExecutions.has(execution.id)}
                addExecutionToSelection={addSelectedExecution}
                removeExecutionFromSelection={removeSelectedExecution}
              />
            ) : (
              <PastExecutionItem execution={execution} />
            )}
          </div>
        ))
      ) : (
        <Typography style={{ textAlign: 'center', marginTop: '16px' }}>
          No executions found.
        </Typography>
      )}
    </Paper>
  )
}
